import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { platformAPI, fileUrl } from '../../services/api';
import { LandingHeader, LandingFooter } from '../landing/LandingChrome';

function resolveVariables(html, settings) {
  if (!html) return '';
  const vars = {
    '{{company_name}}': settings.platform_name || 'LedgerPro',
    '{{support_email}}': settings.support_email || 'support@ledgerpro.com',
    '{{privacy_email}}': settings.privacy_email || settings.support_email || 'privacy@ledgerpro.com',
    '{{security_email}}': settings.security_email || settings.support_email || 'security@ledgerpro.com',
    '{{company_address}}': settings.company_address || '',
    '{{phone}}': settings.company_phone || '',
    '{{website}}': settings.company_website || '',
    '{{COMPANY_NAME}}': (settings.platform_name || 'LedgerPro').toUpperCase(),
    '{{last_updated}}': new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }),
  };
  let result = html;
  for (const [key, val] of Object.entries(vars)) {
    result = result.split(key).join(val);
  }
  return result;
}

const PAGE_META = {
  'privacy-policy': {
    icon: '🔒',
    navLabel: 'Privacy Policy',
    headline: 'Privacy Policy',
    subtitle: 'How LedgerPro collects, uses, protects, and manages your personal and business information.',
    color: '#0ea5e9',
    gradient: 'linear-gradient(135deg, #0c4a6e 0%, #0284c7 100%)',
  },
  'terms-of-service': {
    icon: '📋',
    navLabel: 'Terms of Service',
    headline: 'Terms of Service',
    subtitle: 'The terms and conditions governing your use of LedgerPro Accounting System.',
    color: '#7c3aed',
    gradient: 'linear-gradient(135deg, #4c1d95 0%, #7c3aed 100%)',
  },
  'security': {
    icon: '🛡️',
    navLabel: 'Security',
    headline: 'Security at LedgerPro',
    subtitle: 'Protecting your business data is one of our highest priorities.',
    color: '#059669',
    gradient: 'linear-gradient(135deg, #064e3b 0%, #059669 100%)',
  },
  'contact': {
    icon: '💬',
    navLabel: 'Contact',
    headline: 'Contact Us',
    subtitle: 'Get in touch with our team for support, sales inquiries, or partnership opportunities.',
    color: '#0ea5e9',
    gradient: 'linear-gradient(135deg, #1e3a5f 0%, #0ea5e9 100%)',
  },
};

const STYLES = `
  @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800;900&display=swap');

  .legal-page {
    font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
  }

  .legal-page * { box-sizing: border-box; }

  .legal-hero {
    position: relative;
    padding: 80px 24px 60px;
    text-align: center;
    overflow: hidden;
  }

  .legal-hero::before {
    content: '';
    position: absolute;
    top: 0; left: 0; right: 0; bottom: 0;
    background: radial-gradient(ellipse at 30% 50%, rgba(255,255,255,0.08) 0%, transparent 70%);
    pointer-events: none;
  }

  .legal-hero::after {
    content: '';
    position: absolute;
    bottom: 0; left: 0; right: 0;
    height: 1px;
    background: rgba(255,255,255,0.15);
  }

  .legal-hero-badge {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    background: rgba(255,255,255,0.12);
    backdrop-filter: blur(8px);
    border: 1px solid rgba(255,255,255,0.15);
    border-radius: 100px;
    padding: 6px 16px;
    font-size: 12px;
    font-weight: 600;
    color: rgba(255,255,255,0.9);
    letter-spacing: 0.05em;
    text-transform: uppercase;
    margin-bottom: 20px;
  }

  .legal-hero h1 {
    margin: 0 0 12px;
    font-size: 44px;
    font-weight: 900;
    color: #fff;
    letter-spacing: -0.02em;
    line-height: 1.1;
  }

  .legal-hero-subtitle {
    margin: 0;
    font-size: 17px;
    color: rgba(255,255,255,0.75);
    max-width: 600px;
    margin: 0 auto;
    line-height: 1.6;
    font-weight: 400;
  }

  .legal-hero-meta {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 16px;
    margin-top: 24px;
    font-size: 13px;
    color: rgba(255,255,255,0.6);
  }

  .legal-hero-meta span {
    display: flex;
    align-items: center;
    gap: 5px;
  }

  .legal-layout {
    max-width: 1100px;
    margin: 0 auto;
    padding: 0 24px 80px;
    display: grid;
    grid-template-columns: 240px 1fr;
    gap: 40px;
    align-items: start;
  }

  .legal-toc {
    position: sticky;
    top: 24px;
    padding: 24px 0;
  }

  .legal-toc-title {
    font-size: 11px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    color: #94a3b8;
    margin: 0 0 12px;
    padding: 0 12px;
  }

  .legal-toc-list {
    list-style: none;
    padding: 0;
    margin: 0;
    border-left: 2px solid #e2e8f0;
  }

  .legal-toc-list li { margin: 0; }

  .legal-toc-list a {
    display: block;
    padding: 6px 12px;
    font-size: 13px;
    font-weight: 500;
    color: #64748b;
    text-decoration: none;
    border-left: 2px solid transparent;
    margin-left: -2px;
    transition: all 0.2s ease;
    line-height: 1.4;
  }

  .legal-toc-list a:hover {
    color: #0f172a;
    background: #f8fafc;
  }

  .legal-toc-list a.active {
    color: #2563eb;
    border-left-color: #2563eb;
    font-weight: 600;
    background: #eff6ff;
  }

  .legal-content {
    background: #fff;
    border-radius: 16px;
    padding: 48px 56px;
    box-shadow: 0 1px 3px rgba(0,0,0,0.04), 0 4px 24px rgba(0,0,0,0.02);
    border: 1px solid #e2e8f0;
    line-height: 1.8;
    font-size: 15.5px;
    color: #334155;
  }

  .legal-content h1 {
    font-size: 14px !important;
    font-weight: 700 !important;
    color: #94a3b8 !important;
    text-transform: uppercase !important;
    letter-spacing: 0.06em !important;
    margin: 0 0 8px !important;
    padding: 0 !important;
    border: none !important;
  }

  .legal-content h2 {
    font-size: 22px !important;
    font-weight: 800 !important;
    color: #0f172a !important;
    margin: 40px 0 16px !important;
    padding-bottom: 10px !important;
    border-bottom: 2px solid #f1f5f9 !important;
    letter-spacing: -0.01em;
    scroll-margin-top: 24px;
  }

  .legal-content h2:first-of-type {
    margin-top: 8px !important;
  }

  .legal-content h3 {
    font-size: 17px !important;
    font-weight: 700 !important;
    color: #1e293b !important;
    margin: 28px 0 10px !important;
    scroll-margin-top: 24px;
  }

  .legal-content h4 {
    font-size: 15px !important;
    font-weight: 700 !important;
    color: #334155 !important;
    margin: 20px 0 6px !important;
  }

  .legal-content p {
    margin: 0 0 14px !important;
    color: #475569;
  }

  .legal-content > p:first-of-type {
    color: #64748b;
    font-style: italic;
  }

  .legal-content ul, .legal-content ol {
    margin: 10px 0 16px !important;
    padding-left: 24px !important;
  }

  .legal-content li {
    margin-bottom: 8px !important;
    color: #475569;
  }

  .legal-content li strong {
    color: #0f172a;
  }

  .legal-content strong {
    color: #0f172a;
    font-weight: 700;
  }

  .legal-content a {
    color: #2563eb;
    text-decoration: none;
    font-weight: 500;
    border-bottom: 1px solid transparent;
    transition: border-color 0.2s;
  }

  .legal-content a:hover {
    border-bottom-color: #2563eb;
  }

  .legal-content blockquote {
    margin: 20px 0 !important;
    padding: 16px 20px !important;
    border-left: 4px solid #2563eb !important;
    background: #f8fafc !important;
    border-radius: 0 8px 8px 0 !important;
    color: #475569 !important;
  }

  .legal-content table {
    width: 100% !important;
    border-collapse: collapse !important;
    margin: 20px 0 !important;
    border-radius: 8px;
    overflow: hidden;
    border: 1px solid #e2e8f0;
  }

  .legal-content th {
    padding: 12px 16px !important;
    background: #0f172a !important;
    color: #fff !important;
    font-weight: 700 !important;
    font-size: 13px !important;
    text-align: left !important;
    border: none !important;
    text-transform: uppercase;
    letter-spacing: 0.04em;
  }

  .legal-content td {
    padding: 12px 16px !important;
    border: 1px solid #f1f5f9 !important;
    font-size: 14px !important;
    color: #475569 !important;
  }

  .legal-content tr:nth-child(even) td {
    background: #f8fafc;
  }

  .legal-content hr {
    border: none !important;
    border-top: 1px solid #e2e8f0 !important;
    margin: 32px 0 !important;
  }

  .legal-content em {
    color: #64748b;
    font-size: 14px;
  }

  @media (max-width: 768px) {
    .legal-hero { padding: 60px 20px 40px; }
    .legal-hero h1 { font-size: 32px; }
    .legal-hero-subtitle { font-size: 15px; }
    .legal-layout {
      grid-template-columns: 1fr;
      padding: 0 16px 60px;
      gap: 0;
    }
    .legal-toc {
      position: relative;
      top: 0;
      padding: 20px 0;
      border-bottom: 1px solid #e2e8f0;
      margin-bottom: 24px;
    }
    .legal-content {
      padding: 28px 20px;
      border-radius: 12px;
    }
    .legal-content h2 { font-size: 19px !important; }
    .legal-content th, .legal-content td { padding: 8px 10px !important; font-size: 12px !important; }
    .legal-hero-meta { flex-direction: column; gap: 6px; }
  }

  @media print {
    .legal-hero { padding: 30px 20px 20px !important; break-after: avoid; }
    .legal-toc { display: none !important; }
    .legal-layout {
      grid-template-columns: 1fr !important;
      padding: 0 !important;
    }
    .legal-content {
      box-shadow: none !important;
      border: none !important;
      padding: 0 !important;
    }
    .legal-content h2 { break-after: avoid; }
  }
`;

export default function LegalPage() {
  const params = useParams();
  const slug = params.slug || window.location.pathname.replace(/^\//, '');
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeToc, setActiveToc] = useState('');
  const [platform, setPlatform] = useState({});
  const articleRef = useRef(null);
  const meta = PAGE_META[slug] || { icon: '📄', navLabel: slug, headline: slug, subtitle: '', color: '#64748b', gradient: 'linear-gradient(135deg, #334155 0%, #64748b 100%)' };

  useEffect(() => {
    setLoading(true);
    setError(null);
    platformAPI.getPublicLegal(slug)
      .then(data => {
        const resolved = { ...data };
        resolved.content = resolveVariables(data.content, data);
        resolved.title = resolveVariables(data.title, data);
        setContent(resolved);
      })
      .catch(err => setError(err.message))
      .finally(() => setLoading(false));
    platformAPI.getPublicSettings().then(p => setPlatform(p)).catch(() => {});
  }, [slug]);

  // Extract headings for Table of Contents
  const tocItems = useMemo(() => {
    if (!content?.content) return [];
    const regex = /<h2[^>]*>(.*?)<\/h2>/gi;
    const items = [];
    let match;
    while ((match = regex.exec(content.content)) !== null) {
      const text = match[1].replace(/<[^>]+>/g, '').trim();
      const id = text.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
      items.push({ text, id });
    }
    return items;
  }, [content]);

  // Add IDs to h2 elements after render
  useEffect(() => {
    if (!articleRef.current || tocItems.length === 0) return;
    const h2s = articleRef.current.querySelectorAll('h2');
    h2s.forEach(h2 => {
      const text = h2.textContent.trim();
      const id = text.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
      h2.id = id;
    });
  }, [content, tocItems]);

  // Track active TOC item on scroll
  useEffect(() => {
    if (tocItems.length === 0) return;
    const handleScroll = () => {
      const headings = tocItems.map(item => document.getElementById(item.id)).filter(Boolean);
      for (let i = headings.length - 1; i >= 0; i--) {
        const rect = headings[i].getBoundingClientRect();
        if (rect.top <= 120) {
          setActiveToc(tocItems[i].id);
          return;
        }
      }
      if (headings.length > 0) setActiveToc(tocItems[0].id);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, [tocItems]);

  const otherPages = Object.entries(PAGE_META).filter(([s]) => s !== slug);

  if (loading) {
    return (
      <div className="legal-page" style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f8fafc' }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ width: 40, height: 40, border: '3px solid #e2e8f0', borderTopColor: '#2563eb', borderRadius: '50%', animation: 'spin 0.8s linear infinite', margin: '0 auto 16px' }} />
          <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
          <p style={{ color: '#94a3b8', fontSize: 14, margin: 0 }}>Loading...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="legal-page" style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f8fafc' }}>
        <div style={{ textAlign: 'center', padding: 40 }}>
          <div style={{ fontSize: 48, marginBottom: 16 }}>📄</div>
          <h1 style={{ fontSize: 22, fontWeight: 800, color: '#0f172a', marginBottom: 8, fontFamily: 'Inter, sans-serif' }}>Page Not Found</h1>
          <p style={{ color: '#64748b', marginBottom: 24, fontSize: 15 }}>{error}</p>
          <Link to="/login" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: '#2563eb', fontWeight: 600, fontSize: 14, textDecoration: 'none', padding: '10px 20px', background: '#eff6ff', borderRadius: 8 }}>
            ← Back to Login
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="legal-page" style={{ minHeight: '100vh', background: '#f8fafc' }}>
      <style>{STYLES}</style>

      {/* Shared landing navbar */}
      <LandingHeader />

      {/* Hero */}
      <div className="legal-hero" style={{ background: meta.gradient }}>
        <div className="legal-hero-badge">
          <span>{meta.icon}</span>
          <span>LedgerPro Accounting System</span>
        </div>
        <h1>{content?.title || meta.headline}</h1>
        {meta.subtitle && <p className="legal-hero-subtitle">{meta.subtitle}</p>}
        <div className="legal-hero-meta">
          {content?.updated_at && (
            <span>
              <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10"/><path d="M12 6v6l4 2"/></svg>
              Last updated: {new Date(content.updated_at).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
            </span>
          )}
          <span>
            <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><path d="M14 2v6h6"/></svg>
            Legal Document
          </span>
        </div>
      </div>

      {/* Content Layout */}
      <div className="legal-layout">
        {/* Table of Contents */}
        {tocItems.length > 0 && (
          <aside className="legal-toc">
            <p className="legal-toc-title">On this page</p>
            <ul className="legal-toc-list">
              {tocItems.map(item => (
                <li key={item.id}>
                  <a
                    href={`#${item.id}`}
                    className={activeToc === item.id ? 'active' : ''}
                    onClick={e => {
                      e.preventDefault();
                      document.getElementById(item.id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    }}
                  >
                    {item.text}
                  </a>
                </li>
              ))}
            </ul>
          </aside>
        )}

        {/* Article */}
        <article className="legal-content" ref={articleRef}>
          <div dangerouslySetInnerHTML={{ __html: content?.content || '<p>No content available.</p>' }} />
        </article>
      </div>

      {/* Shared landing footer */}
      <LandingFooter />
    </div>
  );
}
